import { useEffect, useState } from "react";
import { fetchMentorRequests } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Inbox, Clock, CheckCircle2, XCircle, GraduationCap, Send } from "lucide-react";
import MentorRequestRow from "./MentorRequestRow";

/**
 * Mentee Outbox — shown inside the Employee Dashboard. Lists requests where the
 * current employee is the MENTEE (i.e. requests they sent). Expanding a row shows
 * the AI mentee preparation pack.
 */
export default function MenteeOutboxPanel({ employeeId }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, completed: 0, total: 0 });

  const reload = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const all = await fetchMentorRequests({ mentee_id: employeeId });
      const allItems = all.items || [];
      const c = { pending: 0, approved: 0, rejected: 0, completed: 0, total: allItems.length };
      for (const it of allItems) c[it.status] = (c[it.status] || 0) + 1;
      setCounts(c);
      setItems(filter === "all" ? allItems : allItems.filter((i) => i.status === filter));
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, filter]);

  const empty = items.length === 0;

  return (
    <div className="space-y-5" data-testid="mentee-outbox-panel">
      {/* Hero */}
      <div className="card-surface p-6 bg-gradient-to-br from-violet-50 via-white to-white border-violet-200">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-300 flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5 text-violet-700" />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-violet-700 mb-1">Gönderdiğim Talepler</div>
            <h3 className="font-display text-2xl font-semibold tracking-tight">
              {counts.pending > 0 ? (
                <>
                  <span className="text-violet-700">{counts.pending}</span> mentordan onay bekliyorsun
                </>
              ) : counts.approved > 0 ? (
                <>
                  <span className="text-emerald-700">{counts.approved}</span> mentorluğun aktif — görüşmelerine hazırlan
                </>
              ) : counts.total > 0 ? (
                "Gönderdiğin talepleri buradan takip et"
              ) : (
                "Henüz mentor talebi göndermedin"
              )}
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Her talep için AI hazırlık paketin hazır — mentorla görüşmeden önce gözden geçirebilirsin.
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-4 gap-3">
          <Stat label="Onay bekleyen" value={counts.pending} accent="amber" Icon={Clock} />
          <Stat label="Kabul edilen" value={counts.approved} accent="emerald" Icon={CheckCircle2} />
          <Stat label="Reddedilen" value={counts.rejected} accent="rose" Icon={XCircle} />
          <Stat label="Tamamlanan" value={counts.completed} accent="sky" Icon={GraduationCap} />
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white border border-slate-200 mb-4 p-1.5 h-auto flex-wrap">
          <TabsTrigger value="all" data-testid="outbox-filter-all" className="text-sm px-4 py-1.5 data-[state=active]:bg-violet-50 data-[state=active]:text-violet-900 data-[state=active]:shadow-sm">
            Tümü ({counts.total})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="outbox-filter-pending" className="text-sm px-4 py-1.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-900 data-[state=active]:shadow-sm">
            Onay bekleyen ({counts.pending})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="outbox-filter-approved" className="text-sm px-4 py-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm">
            Kabul edilen ({counts.approved})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="outbox-filter-rejected" className="text-sm px-4 py-1.5 data-[state=active]:bg-rose-50 data-[state=active]:text-rose-900 data-[state=active]:shadow-sm">
            Reddedilen ({counts.rejected})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="outbox-filter-completed" className="text-sm px-4 py-1.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-900 data-[state=active]:shadow-sm">
            Tamamlanan ({counts.completed})
          </TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="space-y-3">
          {loading && (
            <div className="card-surface p-8" data-testid="mentee-outbox-loading">
              <div className="h-4 bg-slate-200/60 rounded animate-pulse w-1/3 mb-2" />
              <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
            </div>
          )}
          {!loading && empty && (
            <div className="card-surface p-12 text-center" data-testid="mentee-outbox-empty">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <h3 className="font-display text-lg font-semibold text-slate-900">
                Bu kategoride talep yok
              </h3>
              <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
                Mentorlar sekmesinden bir mentor seçip talep gönderebilirsin. AI hazırlık paketin
                otomatik üretilir, görüşmen için seni hazırlar.
              </p>
            </div>
          )}
          {!loading && items.map((it) => (
            <MentorRequestRow key={it.id} item={it} onUpdate={reload} perspective="mentee_sent" currentUserId={employeeId} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, accent, Icon }) {
  const colors = {
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    emerald: "border-emerald-300 text-emerald-800 bg-emerald-50",
    rose: "border-rose-300 text-rose-800 bg-rose-50",
    sky: "border-sky-300 text-sky-800 bg-sky-50",
  }[accent];
  return (
    <div className={`rounded-lg border ${colors} px-3 py-2.5`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[0.6rem] uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className="font-mono text-xl font-semibold">{value}</div>
    </div>
  );
}
